import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from "@angular/core";
import { AccountCamMicStreamService } from "@youm-front/account-cammic/data-access";
import { AccountDeviceFacade } from "@youm-front/account-device/data-access";
import {
  VideoInputDeviceIsFlipped,
  VideoInputResolution
} from "@youm-front/account-device/domain";
import { BrowserService } from "@youm-front/core";
import { DeviceFacade } from "@youm-front/device/data-access";
import {
  AudioInputDeviceId,
  AudioOutputDeviceId,
  VideoInputDeviceId
} from "@youm-front/device/domain";
import {
  map,
  tap,
  takeUntil,
  mergeMap,
  distinctUntilChanged,
  switchMap
} from "rxjs/operators";
import { BehaviorSubject, EMPTY, merge, Observable, Subject } from "rxjs";
import { ExtendedMediaStream } from "youm-lib";
import WaveSurfer from "wavesurfer.js";
import RecordPlugin from "wavesurfer.js/dist/plugins/record.js";

@Component({
  selector: "app-settings-feature-settings",
  templateUrl: "./settings.component.html",
  styleUrls: ["./settings.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SettingsComponent implements AfterViewInit, OnDestroy, OnInit {
  readonly RESOLUTIONS = [
    {
      title: "Auto",
      value: "auto"
    },
    {
      title: "Full High Definition (1080p @ 30 fps)",
      value: "1080p@30"
    },
    {
      title: "High Definition (720p @ 30 fps)",
      value: "720p@30"
    },
    {
      title: "Standard Definition (480p @ 30 fps)",
      value: "480p@30"
    },
    {
      title: "Low Definition 360p @ 30 fps",
      value: "360p@30"
    },
    {
      title: "Super Low Definition 240p @ 30 fps",
      value: "240p@30"
    }
  ];
  // 'Settings Stream' is a deep copy of an 'Account CamMic Stream',
  // with all tracks enabled, to be able to inspect them.
  // It should be 1:1 track usage with an 'Account CamMic Stream' and
  // should not use any previous track.
  readonly sStream$ = new BehaviorSubject<null | ExtendedMediaStream>(null);

  @ViewChild("audioInputWaveFormRef")
  private audioInputWaveFormRef!: ElementRef<HTMLDivElement>;

  @ViewChild("audioOutputDeviceTestRef")
  private audioOutputDeviceTestRef!: ElementRef<HTMLAudioElement>;

  private readonly _detailsCopied$ = new BehaviorSubject<boolean>(false);
  private readonly _detailsRender$ = new BehaviorSubject<boolean>(false);
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly accountCamMicStreamService: AccountCamMicStreamService,
    private readonly accountDeviceFacade: AccountDeviceFacade,
    private readonly browserService: BrowserService,
    private readonly deviceFacade: DeviceFacade
  ) {}

  ngOnInit() {
    this.accountCamMicStreamService.stream$
      .pipe(
        tap((_) => this.sStreamReCreate(_)),
        switchMap((_) =>
          _
            ? merge(_.addTrack$, _.removeTrack$).pipe(
                tap(() => this.sStreamReCreate(_))
              )
            : EMPTY
        ),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngAfterViewInit() {
    this.audioInputWaveFormInit();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    this.sStream$.value?.destroy();
  }

  get audioInputDevices$() {
    return this.deviceFacade.devices$.pipe(
      map((_) => _.filter((_) => _.kind === "audioinput"))
    );
  }

  get audioInputDeviceId$() {
    return this.accountDeviceFacade.audioInputDeviceId$;
  }

  get audioOutputDevices$() {
    return this.deviceFacade.devices$.pipe(
      map((_) => _.filter((_) => _.kind === "audiooutput"))
    );
  }

  get audioOutputDeviceId$() {
    return this.accountDeviceFacade.audioOutputDeviceId$;
  }

  get audioOutputDeviceIsSupported() {
    return this.browserService.isBlink || this.browserService.isEdge;
  }

  get details$() {
    return this.accountCamMicStreamService.stream$.pipe(
      map((_) =>
        _?.tracks.map((_) => ({
          track: {
            id: _.id,
            contentHint: _.contentHint,
            kind: _.kind,
            label: _.label,
            muted: _.muted,
            readyState: _.readyState
          },
          // 'getCapabilities' does not have a full support
          // https://caniuse.com/?search=getCapabilities
          capabilities: "getCapabilities" in _ ? _.getCapabilities() : {},
          constraints: _.getConstraints(),
          settings: _.getSettings()
        }))
      ),
      // JSON.stringify to have un-formatted json string
      map((_) => (_ ? JSON.stringify(_) : _))
    );
  }

  get detailsCopied$() {
    return this._detailsCopied$.asObservable().pipe(distinctUntilChanged());
  }

  get detailsRender$() {
    return this._detailsRender$.asObservable().pipe(distinctUntilChanged());
  }

  get videoInputDevices$() {
    return this.deviceFacade.devices$.pipe(
      map((_) => _.filter((_) => _.kind === "videoinput"))
    );
  }

  get videoInputDeviceId$() {
    return this.accountDeviceFacade.videoInputDeviceId$;
  }

  get videoInputDeviceIsFlipped$() {
    return this.accountDeviceFacade.videoInputDeviceIsFlipped$;
  }

  get videoInputResolution$(): Observable<"auto" | VideoInputResolution> {
    return this.accountDeviceFacade.videoInputResolution$.pipe(
      map((_) => (_ ? _ : "auto"))
    );
  }

  get videoInputResolutionRequiresCapableDeviceToRun$() {
    return this.videoInputResolution$.pipe(
      map((_) => ["1080p@30"].includes(_))
    );
  }

  onAudioInputDeviceIdSet(deviceId: AudioInputDeviceId) {
    this.accountDeviceFacade.audioInputDeviceIdSet(deviceId);
  }

  onAudioOutputDeviceIdSet(deviceId: AudioOutputDeviceId) {
    this.accountDeviceFacade.audioOutputDeviceIdSet(deviceId);
  }

  onAudioOutputDeviceTest(deviceId: null | string) {
    if (!deviceId) {
      return;
    }

    const audioOutputDeviceTestEl =
      this.audioOutputDeviceTestRef?.nativeElement;
    if (!audioOutputDeviceTestEl) {
      return;
    }

    audioOutputDeviceTestEl
      .setSinkId?.(deviceId)
      .then(() => audioOutputDeviceTestEl.play())
      .catch();
  }

  onDetailsCopied() {
    this._detailsCopied$.next(true);
  }

  onDetailsRenderToggle() {
    this._detailsRender$.next(!this._detailsRender$.value);
  }

  onDetailsUnCopied() {
    this._detailsCopied$.next(false);
  }

  onVideoInputDeviceIdSet(deviceId: VideoInputDeviceId) {
    this.accountDeviceFacade.videoInputDeviceIdSet(deviceId);
  }

  onVideoInputDeviceIsFlippedSet(videoInputDeviceIsFlipped: boolean) {
    this.accountDeviceFacade.videoInputDeviceIsFlippedSet(
      videoInputDeviceIsFlipped as VideoInputDeviceIsFlipped
    );
  }

  onVideoInputResolutionSet(
    videoInputResolution: "auto" | VideoInputResolution
  ) {
    this.accountDeviceFacade.videoInputResolutionSet(
      videoInputResolution === "auto" ? null : videoInputResolution
    );
  }

  private audioInputWaveFormInit() {
    const audioInputWaveFormEl = this.audioInputWaveFormRef?.nativeElement;
    if (!audioInputWaveFormEl) {
      return;
    }

    const wavesurfer = WaveSurfer.create({
      container: audioInputWaveFormEl,
      waveColor: "#ff7f00",
      height: "auto",
      cursorWidth: 0
    });

    const record = wavesurfer.registerPlugin(RecordPlugin.create());

    this.accountDeviceFacade.audioInputDeviceId$
      .pipe(
        tap(() => record.stopMic()),
        mergeMap((_) => (_ ? record.startMic({ deviceId: _ }) : EMPTY)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private sStreamReCreate(origin: null | ExtendedMediaStream) {
    this.sStream$.value?.destroy();

    if (!origin) {
      return;
    }

    const clone = origin.clone();

    clone.videoEnable();
    clone.audioEnable();

    this.sStream$.next(clone);
  }
}
